import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Link2, Loader2, FileText, XCircle, RefreshCw } from "lucide-react";

export function ConstableIntake() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [cc, setCc] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => client.get("/constable-drops", { params: { status } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const attach = async (d) => {
    const v = (cc[d.id] || d.cc_number_requested || "").trim();
    if (!v) { toast.error("Enter the CC# to attach to."); return; }
    setBusy(d.id);
    try { await client.post(`/constable-drops/${d.id}/attach`, { cc_number: v }); toast.success(`Attached to ${v.toUpperCase()}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Attach failed"); }
    finally { setBusy(null); }
  };
  const reject = async (d) => {
    const note = window.prompt("Reason for rejecting this drop (optional):") ?? null;
    if (note === null) return;
    try { await client.post(`/constable-drops/${d.id}/reject`, { note }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };
  const open = async (d, i) => {
    try {
      const r = await client.get(`/constable-drops/${d.id}/files/${i}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const openReceipt = async (d) => {
    try {
      const r = await client.get(`/constable-drops/${d.id}/receipt.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not generate receipt"); }
  };

  return (
    <div data-testid="constable-intake" className="p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Gavel size={16} className="text-[#B98A1E]" />
        <div className="font-head font-bold text-sm text-white">CONSTABLE INTAKE — CIVIL PROCESS DROPS</div>
        <span className="font-mono text-[10px] text-[#6B7280]">No-login uploads from verified constables · attach to a call for service</span>
        <div className="ml-auto flex items-center gap-2">
          {["pending", "attached", "rejected", "all"].map((s) => (
            <button key={s} data-testid={`constable-intake-filter-${s}`} onClick={() => setStatus(s)}
              className={`px-2.5 py-1 text-[11px] font-head uppercase border ${status === s ? "border-[#B98A1E] text-[#B98A1E]" : "border-[#2C2D35] text-[#9CA3AF]"}`}>{s}</button>
          ))}
          <button data-testid="constable-intake-refresh" onClick={load} className="p-1.5 border border-[#2C2D35] text-[#9CA3AF] hover:text-white"><RefreshCw size={12} /></button>
        </div>
      </div>
      {rows.length === 0 && <div data-testid="constable-intake-empty" className="font-mono text-xs text-[#6B7280] border border-[#2C2D35] p-4">No {status === "all" ? "" : status} constable drops.</div>}
      {rows.map((d) => (
        <div key={d.id} data-testid={`constable-drop-${d.drop_no}`} className="border border-[#2C2D35] bg-[#101114] p-3 space-y-2">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className="font-mono text-[#B98A1E] font-bold">{d.drop_no}</span>
            <span className="font-head text-white">{d.constable?.name}</span>
            <span className="font-mono text-[#9CA3AF]">{d.constable?.badge}{d.constable?.agency ? ` · ${d.constable.agency}` : ""}</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 border border-[#2C2D35] text-[#C9CDD4]">{d.doc_type}</span>
            <span className={`font-mono text-[10px] px-1.5 py-0.5 uppercase ${d.status === "attached" ? "bg-[#10B981]/15 text-[#10B981]" : d.status === "rejected" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
              {d.status}{d.cc_number ? ` → ${d.cc_number}` : ""}</span>
            {d.service_number && <span className="font-mono text-[10px] px-1.5 py-0.5 border border-[#8B5CF6]/50 text-[#A78BFA]">{d.service_number}{d.service_closed ? " · SERVED" : ""}</span>}
            <button data-testid={`constable-drop-receipt-${d.drop_no}`} onClick={() => openReceipt(d)} className="font-mono text-[10px] px-1.5 py-0.5 border border-[#2C2D35] text-[#9CA3AF] hover:text-white">Receipt PDF</button>
            <span className="ml-auto font-mono text-[10px] text-[#6B7280]">{d.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
          </div>
          {d.cc_number_requested && !d.cc_number && <div className="font-mono text-[10px] text-[#F59E0B]">Constable entered CC# {d.cc_number_requested} — not found at submission time.</div>}
          {d.description && <div className="font-mono text-[11px] text-[#C9CDD4]">{d.description}</div>}
          <div className="flex flex-wrap gap-1.5">
            {(d.files || []).map((f, i) => (
              <button key={i} data-testid={`constable-drop-file-${d.drop_no}-${i}`} onClick={() => open(d, i)}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono border border-[#D9A441]/50 text-[#D9A441] hover:bg-[#D9A441]/10">
                <FileText size={11} /> {f.filename} <span className="text-[#6B7280]">{(f.size / 1024).toFixed(0)}KB</span>
              </button>
            ))}
          </div>
          {d.status === "pending" && (
            <div className="flex items-center gap-2 pt-1">
              <input data-testid={`constable-drop-cc-${d.drop_no}`} value={cc[d.id] ?? d.cc_number_requested ?? ""} onChange={(e) => setCc((p) => ({ ...p, [d.id]: e.target.value }))}
                placeholder="CC# e.g. 2026-12345" className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white outline-none focus:border-[#B98A1E] w-48" />
              <button data-testid={`constable-drop-attach-${d.drop_no}`} onClick={() => attach(d)} disabled={busy === d.id}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-head font-bold bg-[#B98A1E] text-black disabled:opacity-50">
                {busy === d.id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Attach to call
              </button>
              <button data-testid={`constable-drop-reject-${d.drop_no}`} onClick={() => reject(d)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-head border border-[#2C2D35] text-[#9CA3AF] hover:border-[#EF4444] hover:text-[#EF4444]"><XCircle size={12} /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
